import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as projectApi from '../../services/project-api.js';

@customElement('cp-file-mention-dropdown')
export class CpFileMentionDropdown extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;
  @property({ type: Boolean }) open = false;
  @property({ type: String }) query = '';
  @property({ type: Object }) anchorRect: { top: number; left: number } = { top: 0, left: 0 };

  @state() private _files: string[] = [];
  @state() private _filteredFiles: string[] = [];
  @state() private _loading = false;
  @state() private _selectedIndex = 0;

  private _cachedProjectId = 0;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('projectId') && this.projectId && this.projectId !== this._cachedProjectId) {
      void this._loadFiles();
    }
    if (changed.has('query') || changed.has('_files')) {
      this._filterFiles();
    }
    if (changed.has('open') && this.open) {
      this._selectedIndex = 0;
    }
  }

  private async _loadFiles(): Promise<void> {
    if (!this.projectId) return;
    this._loading = true;
    try {
      this._files = await projectApi.getProjectFiles(this.projectId);
      this._cachedProjectId = this.projectId;
    } catch {
      this._files = [];
    }
    this._loading = false;
  }

  private _filterFiles(): void {
    if (!this.query) {
      this._filteredFiles = this._files.slice(0, 50);
      this._selectedIndex = 0;
      return;
    }

    const q = this.query;
    const scored = this._files
      .map(f => ({ file: f, score: this._scoreFile(f, q) }))
      .filter(entry => entry.score > 0);

    scored.sort((a, b) => b.score - a.score || a.file.localeCompare(b.file));
    this._filteredFiles = scored.slice(0, 50).map(e => e.file);
    this._selectedIndex = 0;
  }

  private _scoreFile(file: string, query: string): number {
    const qLower = query.toLowerCase();
    const fLower = file.toLowerCase();
    const fileName = fLower.substring(fLower.lastIndexOf('/') + 1);

    // Mode 1: Glob *.ext or extension .ext
    if (qLower.startsWith('*.') || (qLower.startsWith('.') && !qLower.includes('/'))) {
      const ext = qLower.startsWith('*') ? qLower.substring(1) : qLower;
      return fLower.endsWith(ext) ? 10 : 0;
    }

    // Mode 2: Path glob with * (e.g. src/server/*.ts)
    if (qLower.includes('/') && qLower.includes('*')) {
      const escaped = qLower.replace(/[.+^${}()|[\]\\]/g, '\\$&');
      const pattern = escaped.replace(/\*/g, '[^/]*');
      try {
        const re = new RegExp('^' + pattern + '$', 'i');
        return re.test(file) ? 10 : 0;
      } catch {
        return 0;
      }
    }

    // Mode 3: Fuzzy path search
    let best = 0;

    // Exact filename match
    if (fileName === qLower) {
      best = 20;
    }
    // Query contained in filename
    else if (fileName.includes(qLower)) {
      best = Math.max(best, 15);
    }

    // Query as substring in full path
    if (fLower.includes(qLower)) {
      best = Math.max(best, 10);
    }

    // Segment-order matching: all segments appear in order
    if (qLower.includes('/') && best < 5) {
      const segments = qLower.split('/').filter(Boolean);
      let pos = 0;
      let allFound = true;
      for (const seg of segments) {
        const idx = fLower.indexOf(seg, pos);
        if (idx === -1) { allFound = false; break; }
        pos = idx + seg.length;
      }
      if (allFound) best = Math.max(best, 5);
    }

    return best;
  }

  handleKeydown(e: KeyboardEvent): boolean {
    if (!this.open) return false;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._selectedIndex = Math.min(this._selectedIndex + 1, this._filteredFiles.length - 1);
      this._scrollActiveIntoView();
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._selectedIndex = Math.max(this._selectedIndex - 1, 0);
      this._scrollActiveIntoView();
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (this._filteredFiles.length > 0) {
        e.preventDefault();
        this._selectFile(this._filteredFiles[this._selectedIndex]);
        return true;
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this.dispatchEvent(new CustomEvent('mention-dismissed', { bubbles: true, composed: true }));
      return true;
    }
    return false;
  }

  private _scrollActiveIntoView(): void {
    this.updateComplete.then(() => {
      const active = this.querySelector('.cp-file-mention-dropdown__item--active');
      active?.scrollIntoView({ block: 'nearest' });
    });
  }

  private _selectFile(filePath: string): void {
    this.dispatchEvent(new CustomEvent('file-selected', {
      bubbles: true,
      composed: true,
      detail: { path: filePath },
    }));
  }

  private _onItemClick(filePath: string): void {
    this._selectFile(filePath);
  }

  private _renderFileName(filePath: string) {
    const isDir = filePath.endsWith('/');
    const clean = isDir ? filePath.slice(0, -1) : filePath;
    const lastSlash = clean.lastIndexOf('/');

    if (lastSlash === -1) {
      return html`<span class="cp-file-mention-dropdown__item-name">${isDir ? clean + '/' : clean}</span>`;
    }
    const dir = clean.substring(0, lastSlash + 1);
    const name = clean.substring(lastSlash + 1);
    return html`
      <span class="cp-file-mention-dropdown__item-name">${name}${isDir ? '/' : ''}</span>
      <span class="cp-file-mention-dropdown__item-dir">${dir}</span>
    `;
  }

  render() {
    if (!this.open || !this.projectId) return nothing;

    const style = `top: ${this.anchorRect.top + 20}px; left: ${this.anchorRect.left}px;`;

    if (this._loading) {
      return html`
        <div class="cp-file-mention-dropdown" style=${style}>
          <div class="cp-file-mention-dropdown__loading">Lade Dateien...</div>
        </div>
      `;
    }

    if (this._filteredFiles.length === 0) {
      return html`
        <div class="cp-file-mention-dropdown" style=${style}>
          <div class="cp-file-mention-dropdown__empty">Keine Dateien gefunden</div>
        </div>
      `;
    }

    return html`
      <div class="cp-file-mention-dropdown" style=${style}>
        ${this._filteredFiles.map((f, i) => html`
          <button
            class="cp-file-mention-dropdown__item ${i === this._selectedIndex ? 'cp-file-mention-dropdown__item--active' : ''}"
            @click=${() => this._onItemClick(f)}
          >${this._renderFileName(f)}</button>
        `)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-file-mention-dropdown': CpFileMentionDropdown;
  }
}
